import React, { useState } from 'react'
import { DataTable, Button, PageHeader } from '@/shared/ui'
import type { AdminRole, Column } from '@/shared/api'
import { RoleFormModal } from './RoleFormModal'
import {
  useAdminRoles, useCreateAdminRole, useUpdateAdminRole, useDeleteAdminRole,
} from '@/shared/hooks'

export const RolesPage: React.FC = () => {
  const [formOpen, setFormOpen] = useState(false)
  const [editRole, setEditRole] = useState<AdminRole | null>(null)

  const { data: roles, isLoading } = useAdminRoles()
  const createRole = useCreateAdminRole()
  const updateRole = useUpdateAdminRole()
  const deleteRole = useDeleteAdminRole()

  const handleSubmit = async (data: { name: string; description: string; permissions: string[] }) => {
    if (editRole) {
      await updateRole.mutateAsync({ id: editRole.admin_role_id, request: data })
    } else {
      await createRole.mutateAsync(data)
    }
    setFormOpen(false)
    setEditRole(null)
  }

  const handleDelete = async (role: AdminRole) => {
    if (!confirm(`Удалить роль «${role.name}»?`)) return
    await deleteRole.mutateAsync(role.admin_role_id)
  }

  const columns: Column<AdminRole>[] = [
    { key: 'name', title: 'Название', render: (_, role) => (
      <div>
        <div className="font-medium text-gray-900">{role.name}</div>
        {role.description && <div className="text-sm text-gray-500">{role.description}</div>}
      </div>
    )},
    { key: 'users_count', title: 'Сотрудников', width: '24' },
    { key: 'permissions', title: 'Права', render: (_, role) =>
      role.is_system ? 'Полный доступ' : `${role.permissions.length} прав` },
    { key: 'actions', title: 'Действия', render: (_, role) => {
      // Системная роль «Администратор» не редактируется и не удаляется
      if (role.is_system) {
        return <span className="text-sm text-gray-400">—</span>
      }
      return (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary"
                  onClick={() => { setEditRole(role); setFormOpen(true) }}>
            Изменить
          </Button>
          <Button size="sm" variant="danger"
                  disabled={role.users_count > 0}
                  onClick={() => handleDelete(role)}>
            Удалить
          </Button>
        </div>
      )
    }},
  ]

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader title="Роли" description="Роли сотрудников «Звена» и их права" />
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button size="sm" className="ml-auto"
                  onClick={() => { setEditRole(null); setFormOpen(true) }}>
            Создать роль
          </Button>
        </div>
        <DataTable data={roles?.items || []} columns={columns} loading={isLoading}
                   emptyMessage="Ролей пока нет" />
      </div>
      <RoleFormModal isOpen={formOpen} role={editRole}
                     loading={createRole.isPending || updateRole.isPending}
                     onClose={() => { setFormOpen(false); setEditRole(null) }}
                     onSubmit={handleSubmit} />
    </div>
  )
}
